import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Alert,
  LinearProgress,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useTemplateExperiments } from '../hooks/useTemplateExperiments';
import {
  DialogHeader,
  LoadingState,
  EmptyState,
  ExperimentCard,
} from './TemplateExperimentsDialogComponents';

/**
 * Template Experiments Dialog Component
 *
 * Displays all A/B testing experiments for a template including:
 * - Experiment list with status
 * - Treatment details (Control vs Variants)
 * - Success metrics
 * - Experiment metadata and dates
 */
const TemplateExperimentsDialog = ({
  open,
  onClose,
  templateId,
  templateName,
}) => {
  const { loading, error, data, fetchExperiments, clearError, clearData } =
    useTemplateExperiments();
  const [refreshLoading, setRefreshLoading] = useState(false);

  useEffect(() => {
    if (open && templateId) {
      fetchExperiments(templateId);
    } else if (!open) {
      clearError();
      clearData();
    }
  }, [open, templateId, fetchExperiments, clearError, clearData]);

  const handleRefresh = async () => {
    if (!templateId) return;
    setRefreshLoading(true);
    await fetchExperiments(templateId);
    setRefreshLoading(false);
  };

  const handleClose = () => {
    clearError();
    clearData();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
        },
      }}
    >
      <DialogHeader templateName={templateName} onClose={handleClose} />

      <DialogContent dividers>
        {loading && !data && <LoadingState />}

        {refreshLoading && data && <LinearProgress sx={{ mb: 2 }} />}

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            action={
              <Button color="inherit" size="small" onClick={handleRefresh}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {data && !loading && (
          <Box>
            {data.length === 0 ? (
              <EmptyState />
            ) : (
              data.map((experiment, index) => (
                <ExperimentCard
                  key={experiment.experiment_id || experiment.id || index}
                  experiment={experiment}
                />
              ))
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleRefresh} disabled={loading || refreshLoading}>
          Refresh
        </Button>
        <Button onClick={handleClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

TemplateExperimentsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  templateId: PropTypes.string.isRequired,
  templateName: PropTypes.string,
};

export default TemplateExperimentsDialog;
